import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { TranslationService } from '@/layout/service/translation.service';
import { PageHeaderComponent } from '@/shared/page-header/page-header.component';
import { EmptyStateComponent, HealthDotComponent, LoadingSkeletonComponent, SectionCardComponent, status } from '@/shared/ui';
import { MQTT_SERVICE, MqttMessage, MqttMonitorResponse, MqttStatus, formatPayload, groupByPrefix, messageMatches } from 'src/api/mqtt';

/** Messages the page keeps; older ones scroll off. */
export const MQTT_MAX_KEPT = 1000;
/** Rows drawn at once, newest first. */
const MAX_SHOWN = 300;
/** Status-only polling: MQTT off, or the user may not see messages. */
const STATUS_POLL_MS = 10_000;
const ERROR_RETRY_MS = 5_000;

type DirectionFilter = 'all' | 'in' | 'out';

/** A message with a key of the page's own: sequence numbers start again when the processor's program restarts. */
export interface MqttRow extends MqttMessage {
    uid: number;
}

/**
 * MQTT tool: the processor's MQTT connection and a live view of the messages going through it, for working out
 * why a sensor is quiet or what it actually sends. Polls through MQTT_SERVICE (processor hub or cloud tunnel);
 * the processor only captures while someone is polling.
 */
@Component({
    selector: 'app-mqtt',
    standalone: true,
    imports: [DatePipe, FormsModule, ButtonModule, TagModule, InputTextModule, SelectModule, MessageModule, TooltipModule, PageHeaderComponent, EmptyStateComponent, HealthDotComponent, LoadingSkeletonComponent, SectionCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (showHeader()) {
            <app-page-header title="mqtt"></app-page-header>
        }

        @if (loading()) {
            <app-loading-skeleton preset="cards" />
        } @else if (!status()) {
            <app-empty-state icon="fa fa-triangle-exclamation" tone="error" [title]="t()('mqtt.loadFailed')" [description]="error()" [actionLabel]="t()('mqtt.retry')" actionIcon="pi pi-refresh" (action)="retry()" />
        } @else {
            @let s = status()!;
            @if (error()) {
                <p-message severity="error" class="mqtt-error">{{ error() }}</p-message>
            }

            <app-section-card [title]="t()('mqtt.connection')" icon="fa fa-tower-broadcast">
                @if (s.mode === 'Off') {
                    <p class="muted mqtt-off">{{ t()('mqtt.off') }}</p>
                } @else {
                    <dl class="mqtt-facts">
                        <dt>{{ t()('mqtt.mode') }}</dt>
                        <dd>{{ t()('mqtt.mode.' + s.mode) }}</dd>
                        <dt>{{ t()('mqtt.state') }}</dt>
                        <dd class="mqtt-connection">
                            <app-health-dot [status]="connectionStatus()" [label]="s.broker" />
                            <span>{{ s.connected ? t()('mqtt.connected') : t()('mqtt.notConnected') }} · {{ s.broker }}</span>
                        </dd>
                        @if (s.connected && s.connectedSinceUtc) {
                            <dt>{{ t()('mqtt.connectedSince') }}</dt>
                            <dd>{{ s.connectedSinceUtc | date: 'medium' }}</dd>
                        }
                        @if (s.lastError && !s.connected) {
                            <dt>{{ t()('mqtt.lastError') }}</dt>
                            <dd class="mqtt-error-text">{{ s.lastError }}</dd>
                        }
                        <dt>{{ t()('mqtt.clientId') }}</dt>
                        <dd>
                            <code>{{ s.clientId }}</code>
                        </dd>
                        @if (s.embeddedBroker; as broker) {
                            <dt>{{ t()('mqtt.broker') }}</dt>
                            <dd>
                                @if (brokerAddress()) {
                                    <code>{{ brokerAddress() }}:{{ broker.port }}</code>
                                } @else {
                                    {{ t()('mqtt.brokerPort') }} {{ broker.port }}
                                }
                                · {{ broker.clients }} {{ t()('mqtt.clients') }}
                            </dd>
                        }
                        <dt>{{ t()('mqtt.subscriptions') }}</dt>
                        <dd>
                            @if (s.subscriptions.length === 0) {
                                <span class="muted">{{ t()('mqtt.noSubscriptions') }}</span>
                            } @else {
                                <button type="button" class="mqtt-link" [attr.aria-expanded]="showSubscriptions()" (click)="showSubscriptions.set(!showSubscriptions())">
                                    {{ s.subscriptions.length }} {{ t()('mqtt.topicFilters') }}
                                    <i [class]="showSubscriptions() ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" aria-hidden="true"></i>
                                </button>
                                @if (showSubscriptions()) {
                                    <ul class="mqtt-subscriptions">
                                        @for (filter of s.subscriptions; track filter) {
                                            <li>
                                                <button type="button" class="mqtt-link" [pTooltip]="t()('mqtt.filterBy')" (click)="topicFilter.set(filter)">
                                                    <code>{{ filter }}</code>
                                                </button>
                                            </li>
                                        }
                                    </ul>
                                }
                            }
                        </dd>
                    </dl>
                }
            </app-section-card>

            @if (s.mode !== 'Off') {
                @if (!messagesAllowed()) {
                    <p-message severity="info" class="mqtt-not-allowed">{{ t()('mqtt.messagesNotAllowed') }}</p-message>
                } @else {
                    <app-section-card [title]="t()('mqtt.messages')" [subtitle]="captureSummary()" icon="fa fa-list" [count]="messages().length || null" [flush]="true">
                        <div actions class="mqtt-actions">
                            @if (paused()) {
                                <p-button icon="pi pi-play" [label]="t()('mqtt.resume')" size="small" [text]="true" (onClick)="resume()" />
                            } @else {
                                <p-button icon="pi pi-pause" [label]="t()('mqtt.pause')" size="small" [text]="true" (onClick)="pause()" />
                            }
                            <p-button icon="pi pi-trash" [label]="t()('mqtt.clear')" size="small" [text]="true" severity="secondary" [disabled]="messages().length === 0" (onClick)="clear()" />
                        </div>

                        <div class="app-card-pad mqtt-toolbar">
                            <span class="mqtt-filter">
                                <i class="pi pi-filter" aria-hidden="true"></i>
                                <input pInputText type="search" [attr.aria-label]="t()('mqtt.filter')" [placeholder]="t()('mqtt.filterPlaceholder')" [ngModel]="topicFilter()" (ngModelChange)="topicFilter.set($event)" />
                            </span>
                            <p-select [options]="directionOptions()" optionLabel="label" optionValue="value" [ngModel]="direction()" (ngModelChange)="direction.set($event)" [ariaLabel]="t()('mqtt.direction')" />
                        </div>

                        @if (missed() > 0) {
                            <p-message severity="warn" class="app-card-pad mqtt-notice">{{ t()('mqtt.missed').replace('{count}', '' + missed()) }}</p-message>
                        }
                        @if (restarted()) {
                            <p-message severity="info" class="app-card-pad mqtt-notice">{{ t()('mqtt.restarted') }}</p-message>
                        }

                        @if (groups().length > 1) {
                            <div class="app-card-pad mqtt-groups" [attr.aria-label]="t()('mqtt.sources')">
                                @for (g of groups(); track g.prefix) {
                                    <button type="button" class="mqtt-group" [class.mqtt-group--active]="topicFilter() === g.prefix + '/#'" (click)="toggleGroup(g.prefix)">
                                        <span>{{ g.prefix }}</span>
                                        <span class="mqtt-group__count">{{ g.count }}</span>
                                    </button>
                                }
                            </div>
                        }

                        @if (messages().length === 0) {
                            <app-empty-state icon="fa fa-satellite-dish" [title]="t()('mqtt.waiting')" [description]="s.connected ? t()('mqtt.waitingDetail.' + s.mode) : t()('mqtt.waitingNotConnected')" [compact]="true" />
                        } @else if (visible().length === 0) {
                            <app-empty-state icon="pi pi-filter-slash" [title]="t()('mqtt.noMatches')" [compact]="true" />
                        } @else {
                            <ul class="mqtt-messages" [attr.aria-label]="t()('mqtt.messages')">
                                @for (m of visible(); track m.uid) {
                                    <li class="mqtt-message" [class.mqtt-message--open]="isExpanded(m)" [attr.data-topic]="m.topic">
                                        <button type="button" class="mqtt-message__row" [attr.aria-expanded]="isExpanded(m)" (click)="toggle(m)">
                                            <span class="mqtt-message__time">{{ m.timestampUtc | date: 'HH:mm:ss.SSS' }}</span>
                                            <i
                                                [class]="m.direction === 'out' ? 'fa fa-arrow-up-right mqtt-message__dir mqtt-message__dir--out' : 'fa fa-arrow-down-left mqtt-message__dir'"
                                                [attr.aria-label]="t()('mqtt.direction.' + m.direction)"
                                                [pTooltip]="t()('mqtt.direction.' + m.direction)"
                                            ></i>
                                            <span class="mqtt-message__topic">{{ m.topic }}</span>
                                            <span class="mqtt-message__preview">{{ preview(m) }}</span>
                                            <span class="mqtt-message__meta">
                                                @if (m.retained) {
                                                    <p-tag [value]="t()('mqtt.retained')" severity="secondary" />
                                                }
                                                <span class="muted">{{ size(m) }}</span>
                                            </span>
                                        </button>
                                        @if (isExpanded(m)) {
                                            <div class="mqtt-message__detail">
                                                <div class="mqtt-message__facts muted">
                                                    <span>{{ m.timestampUtc | date: 'medium' }}</span>
                                                    @if (m.clientId) {
                                                        <span
                                                            >{{ t()('mqtt.from') }} <code>{{ m.clientId }}</code></span
                                                        >
                                                    }
                                                    <span>QoS {{ m.qos }}</span>
                                                    @if (m.truncated) {
                                                        <span class="mqtt-error-text">{{ t()('mqtt.truncated') }}</span>
                                                    }
                                                    <p-button icon="pi pi-copy" [text]="true" size="small" [ariaLabel]="t()('mqtt.copy')" [pTooltip]="t()('mqtt.copy')" (onClick)="copy(m)" />
                                                </div>
                                                <pre class="mqtt-message__payload">{{ payload(m) || t()('mqtt.empty') }}</pre>
                                            </div>
                                        }
                                    </li>
                                }
                            </ul>
                        }
                    </app-section-card>
                }
            }
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: 1rem;
                min-width: 0;
            }
            .muted {
                color: var(--text-color-secondary);
                font-size: 0.875rem;
            }
            .mqtt-off {
                margin: 0;
            }
            .mqtt-facts {
                display: grid;
                grid-template-columns: max-content 1fr;
                gap: 0.4rem 1rem;
                margin: 0;
            }
            .mqtt-facts dt {
                color: var(--text-color-secondary);
            }
            .mqtt-facts dd {
                margin: 0;
                min-width: 0;
                overflow-wrap: anywhere;
            }
            .mqtt-connection {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
            }
            .mqtt-error-text {
                color: var(--app-status-error-fg, var(--red-500));
            }
            .mqtt-link {
                display: inline-flex;
                align-items: center;
                gap: 0.35rem;
                padding: 0;
                border: 0;
                background: none;
                color: var(--primary-color);
                font: inherit;
                cursor: pointer;
                text-align: left;
            }
            .mqtt-subscriptions {
                margin: 0.4rem 0 0;
                padding-left: 1.1rem;
            }
            .mqtt-actions {
                display: flex;
                flex-wrap: wrap;
                gap: 0.25rem;
            }
            .mqtt-toolbar {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
                padding-top: 0.75rem;
                padding-bottom: 0.75rem;
            }
            .mqtt-filter {
                display: inline-flex;
                flex: 1 1 16rem;
                align-items: center;
                gap: 0.5rem;
                min-width: 0;
            }
            .mqtt-filter input {
                flex: 1;
                min-width: 0;
            }
            .mqtt-notice {
                display: block;
                margin-bottom: 0.5rem;
            }
            .mqtt-groups {
                display: flex;
                flex-wrap: wrap;
                gap: 0.4rem;
                padding-bottom: 0.75rem;
            }
            .mqtt-group {
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
                max-width: 100%;
                padding: 0.2rem 0.6rem;
                border: 1px solid var(--surface-border);
                border-radius: 999px;
                background: transparent;
                color: inherit;
                font: inherit;
                font-size: 0.8125rem;
                cursor: pointer;
                overflow-wrap: anywhere;
            }
            .mqtt-group--active {
                border-color: var(--primary-color);
                color: var(--primary-color);
            }
            .mqtt-group__count {
                color: var(--text-color-secondary);
                font-variant-numeric: tabular-nums;
            }
            .mqtt-messages {
                margin: 0;
                padding: 0;
                list-style: none;
            }
            .mqtt-message {
                border-top: 1px solid var(--surface-border);
            }
            .mqtt-message__row {
                display: flex;
                flex-wrap: wrap;
                align-items: baseline;
                gap: 0.25rem 0.75rem;
                width: 100%;
                padding: 0.5rem 1rem;
                border: 0;
                background: none;
                color: inherit;
                font: inherit;
                text-align: left;
                cursor: pointer;
            }
            .mqtt-message__row:hover,
            .mqtt-message--open .mqtt-message__row {
                background: var(--surface-hover);
            }
            .mqtt-message__time {
                font-variant-numeric: tabular-nums;
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }
            .mqtt-message__dir {
                font-size: 0.8125rem;
                color: var(--app-status-success-fg, var(--green-500));
            }
            .mqtt-message__dir--out {
                color: var(--primary-color);
            }
            .mqtt-message__topic {
                font-family: var(--font-family-monospace, monospace);
                font-size: 0.8125rem;
                font-weight: 600;
                overflow-wrap: anywhere;
            }
            .mqtt-message__preview {
                flex: 1 1 12rem;
                min-width: 0;
                overflow: hidden;
                font-family: var(--font-family-monospace, monospace);
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .mqtt-message__meta {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
                margin-left: auto;
            }
            .mqtt-message__detail {
                padding: 0 1rem 0.75rem;
                background: var(--surface-hover);
            }
            .mqtt-message__facts {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.25rem 1rem;
            }
            .mqtt-message__payload {
                max-height: 24rem;
                margin: 0.25rem 0 0;
                padding: 0.75rem;
                overflow: auto;
                border-radius: 6px;
                background: var(--surface-ground);
                font-size: 0.8125rem;
                white-space: pre-wrap;
                overflow-wrap: anywhere;
            }
        `
    ]
})
export class MqttComponent implements OnInit {
    /** The cloud portal shows its own device header. */
    readonly showHeader = input(true);

    private service = inject(MQTT_SERVICE);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;

    readonly status = signal<MqttStatus | null>(null);
    /** Oldest first, at most MQTT_MAX_KEPT. */
    readonly messages = signal<MqttRow[]>([]);
    readonly messagesAllowed = signal(true);
    readonly brokerAddress = signal<string | null>(null);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly paused = signal(false);
    readonly missed = signal(0);
    readonly restarted = signal(false);
    readonly capture = signal<{ startedUtc: string; capacity: number; idleTimeoutSeconds: number } | null>(null);
    /** The filter box: an MQTT topic filter, or text to find. */
    readonly topicFilter = signal('');
    readonly direction = signal<DirectionFilter>('all');
    readonly showSubscriptions = signal(false);
    private readonly expanded = signal<ReadonlySet<number>>(new Set());

    private captureId: string | null = null;
    private nextUid = 0;
    private after = 0;
    private timer: ReturnType<typeof setTimeout> | null = null;
    private destroyed = false;
    private polling = false;

    readonly visible = computed(() => {
        const filter = this.topicFilter();
        const direction = this.direction();
        const shown: MqttRow[] = [];
        const all = this.messages();
        for (let i = all.length - 1; i >= 0 && shown.length < MAX_SHOWN; i--) {
            const m = all[i];
            if ((direction === 'all' || m.direction === direction) && messageMatches(m, filter)) shown.push(m);
        }
        return shown;
    });
    readonly groups = computed(() => groupByPrefix(this.messages()).slice(0, 12));
    readonly connectionStatus = computed(() => (this.status()?.connected ? status('online', this.t()('mqtt.connected')) : status('offline', this.t()('mqtt.notConnected'))));
    readonly directionOptions = computed(() => (['all', 'in', 'out'] as DirectionFilter[]).map((value) => ({ value, label: this.t()('mqtt.direction.' + value) })));
    readonly captureSummary = computed(() => {
        const c = this.capture();
        if (!c) return null;
        const minutes = Math.max(1, Math.round(c.idleTimeoutSeconds / 60));
        return this.t()(this.paused() ? 'mqtt.capturePaused' : 'mqtt.capturing')
            .replace('{capacity}', String(c.capacity))
            .replace('{minutes}', String(minutes));
    });

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.destroyed = true;
            this.stopTimer();
        });
    }

    ngOnInit(): void {
        void this.poll();
    }

    retry(): void {
        this.loading.set(true);
        void this.poll();
    }

    pause(): void {
        this.paused.set(true);
        this.stopTimer();
    }

    resume(): void {
        this.paused.set(false);
        void this.poll();
    }

    clear(): void {
        this.messages.set([]);
        this.missed.set(0);
        this.restarted.set(false);
        this.expanded.set(new Set());
    }

    toggleGroup(prefix: string): void {
        const filter = prefix + '/#';
        this.topicFilter.set(this.topicFilter() === filter ? '' : filter);
    }

    isExpanded(m: MqttRow): boolean {
        return this.expanded().has(m.uid);
    }

    toggle(m: MqttRow): void {
        const next = new Set(this.expanded());
        if (!next.delete(m.uid)) next.add(m.uid);
        this.expanded.set(next);
    }

    preview(m: MqttMessage): string {
        return m.payload.replace(/\s+/g, ' ').slice(0, 200);
    }

    payload(m: MqttMessage): string {
        return formatPayload(m);
    }

    size(m: MqttMessage): string {
        return m.size < 1024 ? `${m.size} B` : `${Math.round((m.size / 1024) * 10) / 10} KB`;
    }

    async copy(m: MqttMessage): Promise<void> {
        try {
            await navigator.clipboard.writeText(m.payload);
        } catch {
            // Clipboard blocked (plain http on the processor): the payload is on screen to select.
        }
    }

    /** One read, then schedule the next. */
    private async poll(): Promise<void> {
        if (this.destroyed || this.polling) return;
        this.stopTimer();
        this.polling = true;
        let delay = this.service.pollIntervalMs;
        try {
            const response = await this.service.monitor(this.captureId, this.after);
            if (this.destroyed) return;
            this.apply(response);
            this.error.set(null);
            if (!response.monitor) delay = STATUS_POLL_MS;
            else if (response.monitor.hasMore) delay = 0;
        } catch (e) {
            this.error.set(MqttComponent.message(e, this.t()('mqtt.noAnswer')));
            delay = ERROR_RETRY_MS;
        } finally {
            this.polling = false;
            this.loading.set(false);
        }
        if (!this.destroyed && !this.paused()) this.timer = setTimeout(() => void this.poll(), delay);
    }

    private apply(response: MqttMonitorResponse): void {
        this.status.set(response.status);
        this.messagesAllowed.set(response.messagesAllowed);
        this.brokerAddress.set(response.brokerAddress ?? null);
        const monitor = response.monitor;
        if (!monitor) {
            this.capture.set(null);
            return;
        }

        const newCapture = monitor.captureId !== this.captureId;
        if (newCapture && this.captureId && monitor.restarted) this.restarted.set(true);
        this.captureId = monitor.captureId;
        this.capture.set({ startedUtc: monitor.captureStartedUtc, capacity: monitor.capacity, idleTimeoutSeconds: monitor.idleTimeoutSeconds });
        if (monitor.missed > 0) this.missed.update((n) => n + monitor.missed);

        const received = monitor.messages ?? [];
        if (received.length) {
            this.after = received[received.length - 1].seq;
            const rows = received.map((m) => ({ ...m, uid: ++this.nextUid }));
            this.messages.update((list) => {
                const next = list.concat(rows);
                return next.length > MQTT_MAX_KEPT ? next.slice(next.length - MQTT_MAX_KEPT) : next;
            });
        } else if (newCapture) {
            this.after = 0;
        }
    }

    private stopTimer(): void {
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
    }

    private static message(e: unknown, fallback: string): string {
        if (typeof e === 'object' && e !== null && 'error' in e) {
            const body = (e as { error?: { error?: string } | string }).error;
            if (typeof body === 'object' && body?.error) return body.error;
            if (typeof body === 'string' && body) return body;
        }
        return e instanceof Error && e.message ? e.message : fallback;
    }
}
